// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import clsx from 'clsx'
import styles from './OwnerRailAsk.module.css'
import OwnerDial from './OwnerDial'
import { ReadBar, ReadLegend } from './ReadBar'
import OwnerHeroAsk from './OwnerHeroAsk'
import { OwnerSentence } from './OwnerFigures'
import {
  ASK_COPY,
  capitalize,
  GATED_ASK_COPY,
  OWNERS_ANCHOR,
  OWNERS_SECTION_ID,
} from 'lib/owner-notes/band-copy'
import { signInGateFor } from 'lib/owner-notes/signin-gate'
import { useWriterHandoff } from 'lib/community/use-writer-handoff'
import { gaveOutCallout, ownedLine, readSplit } from 'lib/owner-notes/owner-figures'
import type { OwnersEnrichment } from 'lib/dealhunt/types'

interface OwnerRailAskProps {
  nodeId: string
  productName: string
  /** This product page — the writer handoff's product (`OwnerHeroAsk`). */
  productHref?: string | null
  /** `enrichment.owners` — the card's data; null or empty is the zero state. */
  owners?: OwnersEnrichment | null
  /** Off the product page (a review): the jump link goes to the PDP's Owners section. */
  ownersHref?: string
  className?: string
}

/**
 * The owner card in the buy rail (design "4b Rail keeps the figures", 2026-09-22): directly
 * under the lab scores card, so the rail reads lab → owners → buy, slimmed to the figures —
 * the dial, the still-using sentence, the reads as micro bars, the jump link, the wear-out
 * line. The `owner-figures.ts` vocabulary from the FIRST note: counts under 20, no comparison
 * with the review score, structured answers only. Owner voice — the notes and the ask — lives
 * under the gallery (`OwnerNotesReader`). Before the first note there is nothing to read, so
 * this card carries the first-owner ask itself.
 */
const OwnerRailAsk: React.VFC<OwnerRailAskProps> = ({
  nodeId,
  productName,
  owners,
  ownersHref = `#${OWNERS_SECTION_ID}`,
  className,
  productHref = null,
}) => {
  const aggregate = owners?.aggregate ?? null
  const count = aggregate?.published_count ?? 0
  const gateMet = owners?.gate_met ?? false
  // The notice follows what the ask opens: `OwnerHeroAsk`'s own handoff (same arguments), whose
  // null-or-not is build flag + product slug only — the same on the server and the client.
  const handoff = useWriterHandoff('rail', productHref, { hash: OWNERS_ANCHOR })
  const askGated = handoff ? signInGateFor('community') : signInGateFor('product')

  if (!aggregate || count === 0) {
    return (
      <section className={clsx(styles.card, className)} aria-label="Owners">
        <header className={styles.head}>
          <span className={styles.heading}>{'Owners'}</span>
          <span className={styles.headMeta}>{'None yet'}</span>
        </header>
        <p className={styles.title}>{'No one has reported on this one yet.'}</p>
        <p className={styles.text}>
          {
            'Be the first. We ask a few quick questions about living with it, then check the review before publishing.'
          }
        </p>
        <OwnerHeroAsk
          nodeId={nodeId}
          productName={productName}
          title={ASK_COPY.title(productName)}
          // Build flag only (cached HTML): the notice follows where the rail ask opens.
          text={askGated ? GATED_ASK_COPY.rail(0) : ASK_COPY.rail(0)}
          location="rail"
          productHref={productHref}
        />
      </section>
    )
  }

  const owned = ownedLine(aggregate)
  const gaveOut = gaveOutCallout(aggregate)
  const answered = aggregate.reads.filter(r => r.answers > 0)
  return (
    <section className={clsx(styles.card, className)} aria-label={`Owners of the ${productName}`}>
      <header className={styles.head}>
        <span className={styles.heading}>{`Owners (${count})`}</span>
        {aggregate.photo_verified > 0 && (
          <span className={styles.headMeta}>{`${aggregate.photo_verified} photo-verified`}</span>
        )}
      </header>
      <div className={styles.lead}>
        <OwnerDial yes={aggregate.buy_again_yes} total={count} gateMet={gateMet} />
        <div className={styles.leadText}>
          <OwnerSentence aggregate={aggregate} gateMet={gateMet} className={styles.sentence} />
          {owned && <p className={styles.fine}>{`${capitalize(owned)}.`}</p>}
        </div>
      </div>
      {answered.length > 0 && (
        <ul className={styles.reads} aria-label="How it compared with expectations">
          {answered.map(read => (
            <li key={read.id} className={styles.read}>
              <div className={styles.readHead}>
                <span className={styles.readLabel}>{read.label}</span>
                <span className={styles.readSplit}>{readSplit(read, gateMet)}</span>
              </div>
              <ReadBar read={read} />
            </li>
          ))}
        </ul>
      )}
      {answered.length > 0 && <ReadLegend className={styles.legend} />}
      <div className={styles.jumpBlock}>
        <a href={ownersHref} className={styles.jump}>
          {`Read the ${count} owner review${count === 1 ? '' : 's'} ${
            ownersHref.startsWith('#') ? '↓' : '→'
          }`}
        </a>
        {gaveOut && <p className={styles.fine}>{`What gave out: ${gaveOut.text}`}</p>}
      </div>
    </section>
  )
}

export default OwnerRailAsk
